<script setup lang="ts">
//#region import
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { useTypedStore } from '@/store';
import { useLodash } from '@/setup/lodash';
import { useApi } from '@/api';
import { type IconControlItem } from '@/types/core';
import type { DashboardPanelTypes, DashboardsStatsTable, FilterRecordExtended } from '@/types/dashboard';
import type { PostDashboardsStatsData } from '@/types/openapi-ts';
//#endregion

//#region types
export interface  Props {
  propCurrencyCode?: string,
  table?: any,
  isEdit?: boolean,
  rowIndex?: number,
  index?: number,
  isLoading?: boolean,
  period?: any[],
}

export interface Emits {
  'removeMetric': [rowIndex: number, type: DashboardPanelTypes, index: number],
  'editMetric': [rowIndex: number, type: DashboardPanelTypes, index: number, data: any], 
  'emitDataReport': [rowIndex: number, type: DashboardPanelTypes, index: number, report: any], 
};
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  propCurrencyCode: '',
  table: () => {},
  isEdit: false,
  isLoading: false,
  period: () => [],
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const store = useTypedStore();
const { t } = useI18n();
const api = useApi();
const _ = useLodash();
//#endregion

//#region state
const sortProp = ref('sortProp');
const sortOrder = ref('asc');
const report = ref({} as DashboardsStatsTable);
const loading = ref(false);
//#endregion

//#region computed
const reportsSettings = computed(() => store.getters['reports/reportsSettings']);
const stateDashboardFilter = computed(() => store.state.dashboards.stateDashboardFilter);

const filterIsEmpty = computed(() => {
  if (props.table.filters === null) return true;
  const { search, filters } = props.table.filters;
  const countKey = Object.keys(props.table.filters).length;
  const func = (count: number, filter: FilterRecordExtended) => {
    const keyArray = Object.keys(filter);
    if (count === 1) {
      return keyArray.includes('filters') || keyArray.includes('search');
    }
    if (count === 2) {
      return keyArray.includes('filters') && keyArray.includes('search');
    }
    return false;
  };
  return (
    _.isEmpty(search)
    && _.isEmpty(filters)
    && func(countKey, props.table.filters)
  );
});

const translateMap = computed(() => {
  return 'dashboards.metric_table';
});

const controlsData = computed<IconControlItem[]>(() => {
  if (!props.isEdit) return [];
  return [
    {
      title: t('dashboards.other.edit'),
      color: 'var(--text-primary)',
      icon: 'pen',
      clickHandler: () => editMetric(props.rowIndex!, 'table', props.index!, props.table),
    },
    {
      title: t('dashboards.other.delete'),
      color: 'var(--text-negative)',
      icon: 'trash-alt',
      clickHandler: () => removeMetric(props.rowIndex!, 'table', props.index!),
    },
  ];
});

const getTableData = computed(() => {
  const { others, data } = _.cloneDeep(report.value);

  if (others) {
    data.push({
      ...others,
      dimension: t('dashboards.other.others'),
    });
  }

  return data;
});

const getTotals = computed(() => {
  return getTotalsValue();
});

const fields = computed(() => {
  return [
    {
      name: 'dimension',
      headerAlign: 'left',
      align: 'left',
    },
    {
      name: 'current',
      align: 'right',
      headerAlign: 'left',
      width: '120',
      computedValue: valueFormatter,
    },
    {
      name: 'previous',
      headerAlign: 'left',
      align: 'right',
      width: '140',
      computedValue: valueFormatter,
    },
    {
      name: 'difference_percent',
      headerAlign: 'left',
      align: 'right',
      width: '110',
      computedValue: percentFormatter,
      computedClass: getComputedClass,
    },
  ];
});

const paramsData = computed(() => {
  const period = {
    from: format(props.period[0],'yyyy-MM-dd HH:mm:ss'),
    to: format(props.period[1], 'yyyy-MM-dd HH:mm:ss'),
  };

  const panel = {
    ...props.table,
    enable_comparison: props.table.enable_comparison || false,
  };

  return {
    panel,
    filters: stateDashboardFilter.value,
    ...period,
    currency_code: props.propCurrencyCode,
  };
});
//#endregion

//#region methods
async function getReport(type?: string) {
  if (type !== 'getLocalData') {
    await getDashboardsStats(paramsData.value);
  } else {
    report.value = props.table.report;
  }

  loading.value = false;
}

async function  getDashboardsStats(params: PostDashboardsStatsData['body']) {
  const {
    data: { payload },
  } = await api.dashboardsStats(params);
  report.value = payload as DashboardsStatsTable;
  emit(
    'emitDataReport',
    props.rowIndex!,
    'table',
    props.index!,
    report.value,
  );
}

function getComputedClass(value: number | null) {
  const defaultClass = 'dashboard-metric-table__table-value';
  if (value === null || value === 0 || value === undefined) return '';
  return value < 0 ? `${defaultClass} dashboard-metric-table__table-value_negative` : `${defaultClass} dashboard-metric-table__table-value_positive`;
}

function valueFormatter(row: any, col: any) {
  if (col === undefined) return '—';
  return col?.formatted || col;
}

function percentFormatter(row: any, col: any) {
  if (col === 0) return `${col}%`;
  return col ? `${col}%` : '—';
}

function getTranslateMetrics(metric: string) {
  return reportsSettings.value.metrics.find((e) => e.column_name === metric);
}

function removeMetric(rowIndex: number, type: DashboardPanelTypes, index: number) {
  emit('removeMetric', rowIndex, type, index);
}

function editMetric(rowIndex: number, type: DashboardPanelTypes, index: number, data: any) {
  emit('editMetric', rowIndex, type, index, data);
}

function getTotalsValue() {
  if (!('totals' in report.value)) return {};
  const {
    current = null,
    previous = null,
    difference_percent = null,
  } = report.value.totals as DashboardsStatsTable['totals'];

  return {
    current: current !== null ? current?.formatted : '—',
    previous: previous !== null ? previous?.formatted : '—',
    difference_percent:
      difference_percent !== null ? `${difference_percent}%` : '—',
  };
}
//#endregion

//#region watch
watch(() => props.propCurrencyCode, () => {
  getReport();
});
//#endregion
defineExpose({
  loading,
  getReport,
});
</script>

<template>
  <div
    :class="[
      'dashboard-metric-table',
      {'dashboard-metric-table_loading':loading || isLoading }
    ]"
  >
    <div class="dashboard-metric-table__title">
      <UiTitle
        :filtered="!filterIsEmpty"
        :tooltip="getTranslateMetrics(table.metric_name)!.description"
        class="t:font-medium"
      >
        {{ table.name }}
      </UiTitle>
      <div
        v-if="isEdit"
        class="t:flex t:space-x-12 t:ml-8"
      >
        <ActionIcon
          v-for="control in controlsData"
          :key="control.title"
          :icon="control.icon"
          :color="control.color"
          :tooltip="control.title"
          :is-overlay="false"
          @click="control.clickHandler"
        />
      </div>
    </div>
    <UiTableNew
      class="dashboard-metric-table__table"
      :select-row="false"
      :fields="fields"
      :data="getTableData"
      :total="getTotals"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      :max-table-height="280"
      :i18n-path="translateMap"
      show-total
    />
  </div>
</template>

<style lang="scss">
.dashboard-metric-table {
  border-radius: 8px;
  background: var(--background-surface);
  min-height: 342px;
  padding: 16px;
  overflow: hidden;
  &__title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  &__table-value {
    span {
      position: relative;
      &:before{
        position: absolute;
        font-family: "Font Awesome 7 Pro";
        font-weight: 900;
        left: -12px;
      }
    }
    &_negative {
      span {
        color: var(--text-negative);
        &:before{
          content: '\f175';
        }
      }
    }
    &_positive {
      span {
        color: var(--text-positive);
        &:before{
          content: '\f176';
        }
      }
    }
  }

  &_loading {
    position: relative;
    &::after {
      content: '';
      z-index: 3;
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background: var(--background-surface);
      background-size: 100% 100%;
      background-position-y: 0;
      background-repeat: no-repeat;
      background-image: url('@/assets/images/dashboard/graph-skeleton.svg');
      padding: 16px;
      background-origin: content-box;
    }
  }
}
</style>
